// app-ui/src/contexts/ThemeContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ColorTheme, colorThemes, getAllThemes } from '@/shared/config/colorThemes';

interface ThemeContextType {
    theme: ColorTheme;
    setTheme: (theme: ColorTheme) => void;
    themeClasses: {
        primary: string;
        secondary: string;
        gradient: string;
        hoverGradient: string;
        ring: string;
        shadow: string;
    };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [theme, setThemeState] = useState<ColorTheme>(() => {
        // Load theme from localStorage or default to purple-blue
        const savedTheme = localStorage.getItem('app-theme') as ColorTheme;
        const allThemes = getAllThemes();
        return savedTheme && allThemes.includes(savedTheme) 
            ? savedTheme 
            : 'purple-blue';
    });

    const setTheme = (newTheme: ColorTheme) => {
        setThemeState(newTheme);
        localStorage.setItem('app-theme', newTheme);
        // Apply theme class to document root for global styling
        document.documentElement.setAttribute('data-theme', newTheme);
    };

    useEffect(() => {
        // Apply theme on mount
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    const themeConfig = colorThemes[theme];

    const value: ThemeContextType = {
        theme,
        setTheme,
        themeClasses: {
            primary: themeConfig.primary,
            secondary: themeConfig.secondary,
            gradient: themeConfig.gradient,
            hoverGradient: themeConfig.hoverGradient,
            ring: themeConfig.ring,
            shadow: themeConfig.shadow,
        },
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Re-export ColorTheme for convenience
export type { ColorTheme } from '@/shared/config/colorThemes';
